/**
 * demoHttpProxy.js — the transport-level interceptor that makes demo mode cover
 * EVERY screen, not just the ones that share a data context.
 *
 * The app has no single HTTP client: dozens of screens and `app/services/*`
 * modules call `global.fetch` (and a few call `axios`) directly. To redact PII
 * everywhere with one hook, we patch the transports themselves — exactly the way
 * the admin panel's `demoHttpProxy` does — and run every JSON response through
 * {@link module:app/demo/demoRedactor}.
 *
 * Safety rails:
 *   • Only touches OUR API hosts (happysolutions.hu / happygastro.hu|org) so
 *     Sentry, Mapbox tiles, analytics, CDNs, etc. are never intercepted.
 *   • A passthrough allow-list leaves auth/session/2fa/password, the current-user
 *     (`/me`), file uploads, streams and the swagger spec completely untouched —
 *     redacting those could break login, session validation or downloads.
 *   • Only `application/json` bodies are reserialised; blobs / receipts / images
 *     pass straight through.
 *   • Fail-open: any error returns the original, unmodified response — demo mode
 *     must never break a request.
 *
 * @module app/demo/demoHttpProxy
 */

import {isDemoMode} from './demoState';
import {redact, redactVenueStored} from './demoRedactor';
import {enrichResponse} from './demoEnricher';

/** Our backend hosts — only these are ever intercepted. */
const API_HOST_RE = /(^|\.)(happysolutions\.hu|happygastro\.hu|happygastro\.org)$/i;

/** Endpoints that must reach the client untouched (auth / identity / binary). */
const PASSTHROUGH_RE = new RegExp([
    '/auth', '/login', '/logout', '/register', '/oauth',
    '/token', 'refresh', '/2fa', '/otp', '/mfa', 'password',
    '/session', 'user/me', '/me(\\b|/|$)',
    'swagger', '/health', '/ping', '/version',
    '/upload', '/stream', '/photos', '/download',
].join('|'), 'i');

/**
 * Extract the host from an absolute URL (RN URLs from these modules are absolute).
 * @param {string} url request URL.
 * @returns {string} the host, or '' when not parseable.
 */
function hostOf(url) {
    const m = /^https?:\/\/([^/:?#]+)/i.exec(String(url || ''));

    return m ? m[1] : '';
}

/**
 * Should this request's response be redacted?
 * @param {string} url request URL.
 * @returns {boolean} true when demo is on, it targets our API, and it's not passthrough.
 */
function shouldIntercept(url) {
    if (!isDemoMode()) {
        return false;
    }
    const u = String(url || '');
    const host = hostOf(u);
    if (!host || !API_HOST_RE.test(host)) {
        return false;
    }

    return !PASSTHROUGH_RE.test(u);
}

/**
 * Patch `global.fetch` so every JSON response from our API is redacted.
 * @returns {void}
 */
function installFetch() {
    if (typeof global.fetch !== 'function' || global.fetch.__demoWrapped) {
        return;
    }

    const realFetch = global.fetch.bind(global);
    const wrapped = async function demoFetch(input, init) {
        const res = await realFetch(input, init);
        try {
            const url = typeof input === 'string' ? input : (input && input.url) || '';
            if (!shouldIntercept(url)) {
                return res;
            }
            const contentType = (res.headers && res.headers.get && res.headers.get('content-type')) || '';
            if (!/application\/json/i.test(contentType)) {
                return res; // blobs / receipts / images / text — leave untouched
            }
            if (typeof global.Response !== 'function' || typeof res.clone !== 'function') {
                return res;
            }
            const json = await res.clone().json();
            const method = (init && init.method)
                || (typeof input === 'object' && input && input.method) || 'GET';
            // Anonymise real data, THEN top up empty lists / synthesise stats so a
            // fresh tenant's screens still look like a busy live system.
            const transformed = enrichResponse(redact(json), url, method);

            return new global.Response(JSON.stringify(transformed), {
                status: res.status,
                statusText: res.statusText,
                headers: res.headers,
            });
        } catch (_) {
            return res; // never break a request because of demo mode
        }
    };
    wrapped.__demoWrapped = true;
    global.fetch = wrapped;
}

/**
 * Add a response interceptor to the default axios instance (covers the screens /
 * payment modules that use `import axios from 'axios'`).
 * @returns {void}
 */
function installAxios() {
    let mod;
    try {
        // eslint-disable-next-line global-require
        mod = require('axios');
    } catch (_) {
        return;
    }
    const inst = (mod && (mod.default || mod)) || null;
    if (!inst || !inst.interceptors || inst.__demoWrapped) {
        return;
    }

    inst.interceptors.response.use((response) => {
        try {
            const cfg = response && response.config;
            const url = cfg ? `${cfg.baseURL || ''}${cfg.url || ''}` : '';
            if (shouldIntercept(url) && response && response.data && typeof response.data === 'object') {
                response.data = enrichResponse(redact(response.data), url, (cfg && cfg.method) || 'GET');
            }
        } catch (_) {
            // fail-open
        }

        return response;
    });
    inst.__demoWrapped = true;
}

/** Stored keys whose display name must be faked (the venue scope). */
const VENUE_STORAGE_KEYS = new Set(['restaurant', 'restaurantPlace']);

/**
 * Patch `AsyncStorage.getItem` so the two venue-scope keys (`restaurant` and
 * `restaurantPlace`) return a copy with a faked display name. These are read raw
 * from storage in ~12 screens (headers, heroes, scope labels, day-close) and
 * never travel through an HTTP response, so the transport proxy alone can't reach
 * them. Ids/scope/config fields are preserved, so tenant scoping is unaffected.
 * @returns {void}
 */
function installAsyncStorage() {
    let mod;
    try {
        // eslint-disable-next-line global-require
        mod = require('@react-native-async-storage/async-storage');
    } catch (_) {
        return;
    }
    const inst = (mod && (mod.default || mod)) || null;
    if (!inst || typeof inst.getItem !== 'function' || inst.__demoVenueWrapped) {
        return;
    }
    const realGetItem = inst.getItem.bind(inst);
    inst.getItem = async function demoGetItem(key, ...rest) {
        const raw = await realGetItem(key, ...rest);
        try {
            if (!isDemoMode() || raw == null || !VENUE_STORAGE_KEYS.has(key)) {
                return raw;
            }
            const faked = redactVenueStored(key, JSON.parse(raw));

            return JSON.stringify(faked);
        } catch (_) {
            return raw; // fail-open — a parse error must never break a storage read
        }
    };
    inst.__demoVenueWrapped = true;
}

let installed = false;

/**
 * Install the transport interceptors once, at boot. Idempotent. The wrappers are
 * always present but no-op unless demo mode is on (checked per request), so this
 * is safe to call unconditionally.
 * @returns {void}
 */
export function installDemoHttpProxy() {
    if (installed) {
        return;
    }
    installed = true;
    installFetch();
    installAxios();
    installAsyncStorage();
}
